/**
 * DeepSearchPage — 59-field power search (§2.31 用户数据查询).
 *
 * Collapsible filter panel with:
 *   - Identity group (id, username, email, phone, invite_code)
 *   - Status group (status, level_code, kyc_status, is_bot)
 *   - Balance range (asset + min/max)
 *   - Financial range (recharge/withdraw min/max)
 *   - Date range (create_time, last_login_at)
 *   - Relations (referrer_id, parent_id, master_id)
 *   - IP search
 * Results in DataTable with expandable balance detail
 */
import { useState, useCallback } from 'react';
import { Button, Card, Col, Collapse, DatePicker, Form, Input, InputNumber, Row, Select, Space, Table, Tag, Modal, Statistic } from 'antd';
import { SearchOutlined, TeamOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { TOKEN_IDS, TOKEN_META, type TokenId } from '@uo/shared';

const STATUS_OPTIONS = [
  { value: 'active', label: '正常' },
  { value: 'pending', label: '待审' },
  { value: 'locked', label: '锁定' },
  { value: 'frozen', label: '冻结' },
  { value: 'banned', label: '封禁' },
];

const KYC_OPTIONS = [
  { value: 'none', label: '未提交' },
  { value: 'pending', label: '审核中' },
  { value: 'approved', label: '已通过' },
  { value: 'rejected', label: '已拒绝' },
];

interface SearchRow {
  id: number;
  username: string;
  email: string | null;
  phone: string | null;
  nickname: string | null;
  invite_code: string;
  level_code: string;
  status: string;
  kyc_status: string;
  is_bot: boolean;
  referrer_id: number | null;
  parent_id: number | null;
  master_id: number | null;
  total_recharge: string;
  total_withdraw: string;
  register_ip: string | null;
  last_login_ip: string | null;
  balances: Record<string, string>;
  create_time: string;
  last_login_at: string | null;
}

export default function DeepSearchPage() {
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<Record<string, any>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [teamModal, setTeamModal] = useState<{ user_id: number; username: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['deep-search', searchParams, page, pageSize],
    queryFn: async () => {
      const params: any = { ...searchParams, page, limit: pageSize };
      // Clean empty values
      for (const k of Object.keys(params)) {
        if (params[k] === undefined || params[k] === null || params[k] === '') {
          delete params[k];
        }
      }
      const res: any = await api.get('/api/admin/deep-search', { params });
      return res.data as { total: number; rows: SearchRow[] };
    },
    staleTime: 30_000,
  });

  const { data: teamStats } = useQuery({
    queryKey: ['team-stats', teamModal?.user_id],
    queryFn: async () => {
      if (!teamModal) return null;
      const res: any = await api.get('/api/admin/deep-search/team-stats', {
        params: { user_id: teamModal.user_id },
      });
      return res.data;
    },
    enabled: !!teamModal,
  });

  const handleSearch = useCallback(() => {
    const values = form.getFieldsValue();
    const params: Record<string, any> = {};

    // Simple string/number fields
    for (const key of ['id', 'username', 'email', 'phone', 'nickname', 'invite_code',
      'status', 'level_code', 'kyc_status', 'is_bot',
      'referrer_id', 'parent_id', 'master_id',
      'balance_asset', 'balance_min', 'balance_max',
      'recharge_min', 'recharge_max', 'withdraw_min', 'withdraw_max',
      'register_ip', 'last_login_ip',
      'sort_by', 'sort_order']) {
      if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
        params[key] = values[key];
      }
    }

    // Date ranges
    if (values.created_range?.[0]) params.created_after = values.created_range[0].format('YYYY-MM-DD');
    if (values.created_range?.[1]) params.created_before = values.created_range[1].format('YYYY-MM-DD');
    if (values.login_range?.[0]) params.login_after = values.login_range[0].format('YYYY-MM-DD');
    if (values.login_range?.[1]) params.login_before = values.login_range[1].format('YYYY-MM-DD');

    setPage(1);
    setSearchParams(params);
  }, [form]);

  const fmt = (v: string) => {
    const n = parseFloat(v);
    return isNaN(n) ? '0' : n.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70, fixed: 'left' as const },
    { title: '用户名', dataIndex: 'username', width: 120, fixed: 'left' as const },
    { title: '邮箱', dataIndex: 'email', width: 160, ellipsis: true },
    { title: '手机', dataIndex: 'phone', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v: string) => {
        const colors: Record<string, string> = { active: 'green', frozen: 'red', banned: 'red', locked: 'orange', pending: 'blue' };
        return <Tag color={colors[v] ?? 'default'}>{v}</Tag>;
      },
    },
    { title: '等级', dataIndex: 'level_code', width: 70 },
    { title: 'KYC', dataIndex: 'kyc_status', width: 80 },
    {
      title: '虚拟',
      dataIndex: 'is_bot',
      width: 60,
      render: (v: boolean) => v ? <Tag color="orange">是</Tag> : null,
    },
    { title: '推荐人', dataIndex: 'referrer_id', width: 80 },
    { title: '总充值', dataIndex: 'total_recharge', width: 110, render: fmt },
    { title: '总提现', dataIndex: 'total_withdraw', width: 110, render: fmt },
    {
      title: '注册时间',
      dataIndex: 'create_time',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, row: SearchRow) => (
        <Button size="small" icon={<TeamOutlined />} onClick={() => setTeamModal({ user_id: row.id, username: row.username })}>
          团队
        </Button>
      ),
    },
  ];

  const expandedRowRender = (row: SearchRow) => {
    const balData = TOKEN_IDS
      .filter((t: TokenId) => parseFloat(row.balances[t] || '0') > 0)
      .map((t: TokenId) => ({
        key: t,
        symbol: TOKEN_META[t].symbol,
        name: TOKEN_META[t].name,
        balance: row.balances[t],
      }));

    return (
      <div style={{ padding: '8px 0' }}>
        <Space wrap>
          <span><strong>邀请码:</strong> {row.invite_code}</span>
          <span><strong>昵称:</strong> {row.nickname || '-'}</span>
          <span><strong>注册IP:</strong> {row.register_ip || '-'}</span>
          <span><strong>登录IP:</strong> {row.last_login_ip || '-'}</span>
          <span><strong>最后登录:</strong> {row.last_login_at ? new Date(row.last_login_at).toLocaleString('zh-CN') : '-'}</span>
        </Space>
        {balData.length > 0 && (
          <Table
            dataSource={balData}
            columns={[
              { title: '代币', dataIndex: 'symbol', width: 80 },
              { title: '名称', dataIndex: 'name', width: 100 },
              { title: '余额', dataIndex: 'balance', width: 150, render: fmt },
            ]}
            rowKey="key"
            pagination={false}
            size="small"
            style={{ marginTop: 8 }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <Collapse
        defaultActiveKey={['filters']}
        style={{ marginBottom: 16 }}
        items={[{
          key: 'filters',
          label: '搜索条件',
          children: (
            <Form form={form} layout="vertical" size="small">
              {/* Identity */}
              <Row gutter={12}>
                <Col span={3}><Form.Item name="id" label="用户ID"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                <Col span={4}><Form.Item name="username" label="用户名"><Input placeholder="模糊搜索" /></Form.Item></Col>
                <Col span={4}><Form.Item name="email" label="邮箱"><Input placeholder="模糊搜索" /></Form.Item></Col>
                <Col span={4}><Form.Item name="phone" label="手机"><Input placeholder="模糊搜索" /></Form.Item></Col>
                <Col span={3}><Form.Item name="invite_code" label="邀请码"><Input /></Form.Item></Col>
                <Col span={3}><Form.Item name="status" label="状态"><Select allowClear options={STATUS_OPTIONS} /></Form.Item></Col>
                <Col span={3}><Form.Item name="level_code" label="等级"><Input placeholder="如 A3" /></Form.Item></Col>
              </Row>

              {/* Status + Relations */}
              <Row gutter={12}>
                <Col span={3}><Form.Item name="kyc_status" label="KYC"><Select allowClear options={KYC_OPTIONS} /></Form.Item></Col>
                <Col span={3}><Form.Item name="is_bot" label="虚拟用户"><Select allowClear options={[{ value: 'true', label: '是' }, { value: 'false', label: '否' }]} /></Form.Item></Col>
                <Col span={3}><Form.Item name="referrer_id" label="推荐人ID"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                <Col span={3}><Form.Item name="parent_id" label="父节点ID"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                <Col span={3}><Form.Item name="master_id" label="主账号ID"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                <Col span={3}><Form.Item name="register_ip" label="注册IP"><Input /></Form.Item></Col>
                <Col span={3}><Form.Item name="last_login_ip" label="登录IP"><Input /></Form.Item></Col>
              </Row>

              {/* Balance range */}
              <Row gutter={12}>
                <Col span={4}><Form.Item name="balance_asset" label="余额币种"><Select allowClear options={TOKEN_IDS.map((t: TokenId) => ({ value: t, label: TOKEN_META[t].symbol }))} /></Form.Item></Col>
                <Col span={3}><Form.Item name="balance_min" label="余额下限"><Input placeholder="0" /></Form.Item></Col>
                <Col span={3}><Form.Item name="balance_max" label="余额上限"><Input placeholder="∞" /></Form.Item></Col>
                <Col span={3}><Form.Item name="recharge_min" label="充值下限"><Input /></Form.Item></Col>
                <Col span={3}><Form.Item name="recharge_max" label="充值上限"><Input /></Form.Item></Col>
                <Col span={3}><Form.Item name="withdraw_min" label="提现下限"><Input /></Form.Item></Col>
                <Col span={3}><Form.Item name="withdraw_max" label="提现上限"><Input /></Form.Item></Col>
              </Row>

              {/* Date range + sort */}
              <Row gutter={12}>
                <Col span={6}><Form.Item name="created_range" label="注册时间"><DatePicker.RangePicker style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={6}><Form.Item name="login_range" label="最后登录"><DatePicker.RangePicker style={{ width: '100%' }} /></Form.Item></Col>
                <Col span={4}>
                  <Form.Item name="sort_by" label="排序字段" initialValue="id">
                    <Select options={[
                      { value: 'id', label: 'ID' },
                      { value: 'create_time', label: '注册时间' },
                      { value: 'last_login_at', label: '最后登录' },
                      { value: 'total_recharge', label: '总充值' },
                      { value: 'total_withdraw', label: '总提现' },
                      { value: 'uo', label: 'ORB余额' },
                      { value: 'ep', label: 'EP余额' },
                    ]} />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item name="sort_order" label="排序方向" initialValue="desc">
                    <Select options={[{ value: 'desc', label: '降序' }, { value: 'asc', label: '升序' }]} />
                  </Form.Item>
                </Col>
                <Col span={5} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
                    <Button onClick={() => { form.resetFields(); setSearchParams({}); }}>重置</Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          ),
        }]}
      />

      {/* Results */}
      <Card size="small" title={`搜索结果 (${data?.total ?? 0} 条)`}>
        <Table
          dataSource={data?.rows ?? []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1400 }}
          expandable={{ expandedRowRender }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          size="small"
        />
      </Card>

      {/* Team stats modal */}
      <Modal
        title={`团队统计 — ${teamModal?.username ?? ''}`}
        open={!!teamModal}
        onCancel={() => setTeamModal(null)}
        footer={null}
      >
        {teamStats && (
          <Row gutter={16}>
            <Col span={12}>
              <Statistic title="直推人数" value={teamStats.direct_referrals} />
            </Col>
            <Col span={12}>
              <Statistic title="团队总人数" value={teamStats.total_team} />
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
}
